import { Card, Typography } from "antd";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { CHART_COLORS } from "../StatsAndAnalytics/utils/chartColors";
import { ActiveFilter } from "../../../../Hooks/useApplicationFilters";

const { Text } = Typography;

interface DoughnutChartCardProps {
  title: string;
  data: Array<{
    name: string;
    value: number;
    color: string;
    status: string;
    isActive: boolean;
  }>;
  total: number;
  cardType: ActiveFilter["cardType"];
  onSegmentClick: (cardType: ActiveFilter["cardType"], status: string) => void;
  activeFilter: ActiveFilter | null;
  activeFiltersList: Array<{ key: string; value: string; label: string }>;
  onRemoveFilter: (key: string) => void;
}

const DoughnutChartCard: React.FC<DoughnutChartCardProps> = ({
  title,
  data,
  total,
  cardType,
  onSegmentClick,
  activeFilter,
}) => {
  const safeData = Array.isArray(data) ? data : [];
  const filteredData = safeData;

  const handleLegendClick = (status: string) => {
    if (onSegmentClick) {
      onSegmentClick(cardType, status);
    }
  };

  return (
    <Card
      className={`application-stat-card application-stat-card--doughnut ${
        activeFilter?.cardType === cardType
          ? "application-stat-card--active"
          : ""
      }`}
      size="small"
    >
      <div className="application-stat-card__doughnut-content">
        <Text strong className="application-stat-card__title">
          {title}
        </Text>
        <div className="application-stat-card__doughnut-body">
          <div className="application-stat-card__chart-wrapper">
            <ResponsiveContainer width="100%" height={100}>
              <PieChart>
                <Pie
                  data={
                    filteredData.length > 0
                      ? filteredData
                      : [
                          {
                            name: "No Data",
                            value: 1,
                            color: CHART_COLORS.placeholder,
                            status: "",
                          },
                        ]
                  }
                  cx="50%"
                  cy="50%"
                  innerRadius={28}
                  outerRadius={42}
                  paddingAngle={2}
                  dataKey="value"
                  labelLine={false}
                  label={false}
                >
                  {(filteredData.length > 0
                    ? filteredData
                    : [{ color: CHART_COLORS.placeholder, status: "no-data" }]
                  ).map((entry, index) => (
                    <Cell
                      key={`cell-${entry.status || entry.color || index}`}
                      fill={entry.color}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [value, ""]}
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #d9d9d9",
                    borderRadius: "6px",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="application-stat-card__chart-center">
              <Text strong className="application-stat-card__chart-total">
                {total}
              </Text>
              <Text
                type="secondary"
                className="application-stat-card__chart-label"
              >
                Total
              </Text>
            </div>
          </div>
          <div className="application-stat-card__legend">
            {filteredData.map((item) => (
              <button
                key={`${item.status}-${item.value}`}
                type="button"
                className={`application-stat-card__legend-item ${
                  item.isActive
                    ? "application-stat-card__legend-item--active"
                    : ""
                }`}
                onClick={() => handleLegendClick(item.status)}
              >
                <span
                  className="application-stat-card__legend-color"
                  style={{ backgroundColor: item.color }}
                />
                <Text
                  type="secondary"
                  className="application-stat-card__legend-text"
                  style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
                >
                  {item.name}: {item.value}
                </Text>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default DoughnutChartCard;
